import { getPreferences } from '@atlassian/browser-storage-controls/controllers/preferences-controls/get-preferences';
import { PreferenceCategory } from '@atlassian/browser-storage-controls/types';

/**
 * Returns whether the user has consented to `FUNCTIONAL`-category browser storage.
 *
 * All cross-join nudge suppression state (snooze timer, view count, opt-out) is written to
 * localStorage keys registered as `FUNCTIONAL` in the browser-storage-controls (BSC) registry.
 * When the user has not granted functional consent, BSC silently drops those writes:
 * `setStorageItem` returns `Status.BLOCKED` and never calls through to the underlying storage.
 * `StorageManager.setItem` resolves that result in a fire-and-forget promise, so callers cannot
 * observe the failure. Reads, however, bypass consent entirely — they go through the raw read
 * storage — so a blocked write looks identical to "never written".
 *
 * The practical consequence is that a dismissal cannot be persisted, and the nudge redisplays on
 * every page load. Callers should treat `false` as "we cannot honour a dismissal" and skip the
 * nudge entirely rather than showing one the user is unable to permanently dismiss.
 *
 * Note this cannot be detected by round-tripping the CJN eligibility cache: that key is registered
 * `STRICTLY_NECESSARY`, which bypasses the consent check and therefore always writes successfully.
 *
 * Fails closed — returns `false` when preferences cannot be resolved or no consent record exists.
 * This matches BSC's own posture in `isProcessingAllowedForCategoryAndPreferenceSync`, which
 * denies processing when preferences are `undefined`.
 */
export const canPersistFunctionalStorage = async (): Promise<boolean> => {
	try {
		const preferences = await getPreferences();

		return preferences?.[PreferenceCategory.Functional] === true;
	} catch (e) {
		// Consent state is unknown, so we cannot guarantee a dismissal would be persisted.
		return false;
	}
};
